import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map, Observable, of, shareReplay} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ForecastSnapshot, ReihenForecast, snapshotKey} from '../../model/forecast-snapshot.model';
import {BoxOrt, WindValues} from './station-forecast.service';

/**
 * Holt die Grafik-Werte in EINEM Aufruf.
 *
 * Bisher fragte jede Box ihren Wert selbst ab – bei über tausend gebundenen Boxen sind
 * das entsprechend viele HTTP-Aufrufe beim Öffnen der Seite. Dieser Service lädt
 * stattdessen einmal {@code GET forecast/snapshot}: der Server liefert den Stand, den
 * sein stündlicher Refresh in Redis abgelegt hat, und alle Boxen lösen ihre Werte
 * daraus lokal auf.
 *
 * <b>Bewusst getrennt von {@link StationForecastService}.</b> Dort steht unverändert
 * die alte Logik mit einem Request je Box. Welche von beiden läuft, entscheidet
 * {@code environment.useForecastSnapshot}; die alte ist damit jederzeit erreichbar,
 * ohne dass hier etwas zurückgebaut werden muss.
 *
 * Der Aufruf wird über {@code shareReplay} geteilt: die Grafik-Seite baut über 200
 * Bild-Komponenten auf, die alle denselben Snapshot brauchen, aber nur einen Request
 * auslösen.
 */
@Injectable({
  providedIn: 'root'
})
export class ForecastSnapshotService {

  private readonly apiUrl = environment.terminalApiUrl;

  /** Der geteilte Aufruf. Erst beim ersten Abonnenten gestartet. */
  private snapshot$?: Observable<ForecastSnapshot | null>;

  /** Letzter erfolgreich geladener Snapshot – Grundlage der synchronen Lookups. */
  private geladen: ForecastSnapshot | null = null;

  constructor(private http: HttpClient) {
  }

  /**
   * Lädt den Snapshot (beim ersten Aufruf) und liefert ihn geteilt an alle Abonnenten.
   *
   * Liefert {@code null}, wenn der Server noch keinen Snapshot hat (HTTP 204, also noch
   * kein Refresh gelaufen) oder der Aufruf fehlschlägt. Der Aufrufer soll in diesem Fall
   * auf die alte Logik zurückfallen – deshalb ist das kein Fehler, sondern ein Ergebnis.
   */
  load(): Observable<ForecastSnapshot | null> {
    if (!this.snapshot$) {
      this.snapshot$ = this.http
        .get<ForecastSnapshot>(`${this.apiUrl}/forecast/snapshot`)
        .pipe(
          map(snapshot => this.uebernehmen(snapshot)),
          catchError(err => {
            console.warn(`Forecast-Snapshot nicht ladbar (Status ${err?.status}) – `
              + 'die Boxen holen ihre Werte einzeln.');
            return of(null);
          }),
          shareReplay({bufferSize: 1, refCount: false})
        );
    }
    return this.snapshot$;
  }

  /**
   * Verwirft den geladenen Snapshot, damit der nächste {@link load} neu lädt.
   * Hängt am Button "MSWR-MOS laden".
   */
  reload(): void {
    this.snapshot$ = undefined;
    this.geladen = null;
  }

  /** Ist ein Snapshot geladen? */
  vorhanden(): boolean {
    return this.geladen !== null;
  }

  /** Zeitpunkt des serverseitigen Refresh, oder null wenn nichts geladen ist. */
  erstellt(): string | null {
    return this.geladen?.erstellt ?? null;
  }

  /**
   * Wettersymbol einer Box aus dem geladenen Snapshot.
   *
   * @param ort   Region (z.B. 'Rheinland') oder – Lokalzeit-Boxen – Station
   * @param reihe Überschrift der Karten-Reihe, z.B. 'HEUTE: ganz'
   * @return Symbol-Code, oder null wenn Snapshot, Reihe oder Region/Station fehlen
   */
  symbol(ort: BoxOrt, reihe: string): number | null {
    const werte = this.reihenWerte(reihe);
    const symbol = ort.region
      ? werte?.symbole?.[snapshotKey(ort.region)]
      : werte?.symboleJeStation?.[ort.station ?? ''];
    return symbol ?? null;
  }

  /**
   * Temperatur einer Box aus dem geladenen Snapshot.
   *
   * @param ort   Region (z.B. 'Kölner Bucht') oder – Lokalzeit-Boxen – Station
   * @param reihe Überschrift der Karten-Reihe, z.B. 'HEUTE: ganz'
   * @return Temperatur in ganzen Grad, oder null wenn Snapshot, Reihe oder Region/Station fehlen
   */
  temperatur(ort: BoxOrt, reihe: string): number | null {
    const werte = this.reihenWerte(reihe);
    const wert = ort.region
      ? werte?.temperaturen?.[snapshotKey(ort.region)]
      : werte?.temperaturenJeStation?.[ort.station ?? ''];
    return wert ?? null;
  }

  /**
   * Wind-Werte einer Box aus dem geladenen Snapshot.
   *
   * @return Werte, oder null wenn Snapshot, Reihe oder Region/Station fehlen
   */
  wind(ort: BoxOrt, reihe: string): WindValues | null {
    const werte = this.reihenWerte(reihe);
    return (ort.region
      ? werte?.wind?.[snapshotKey(ort.region)]
      : werte?.windJeStation?.[ort.station ?? '']) ?? null;
  }

  private reihenWerte(reihe: string): ReihenForecast | undefined {
    return this.geladen?.nachReihe?.[snapshotKey(reihe)];
  }

  /** Merkt den geladenen Snapshot für die synchronen Lookups. */
  private uebernehmen(snapshot: ForecastSnapshot | null): ForecastSnapshot | null {
    this.geladen = snapshot ?? null;
    if (snapshot) {
      console.info(`Forecast-Snapshot geladen: ${snapshot.reihen} Reihen, `
        + `${snapshot.symbolWerte} Symbol-, ${snapshot.temperaturWerte} Temperatur- `
        + `und ${snapshot.windWerte} Wind-Werte, ${snapshot.stationsWerte ?? 0} Lokalzeit-Werte, `
        + `Stand ${snapshot.erstellt}`);
    }
    return this.geladen;
  }
}
